import { accessToken, login } from '@/auth/oidc'
import { ApiError, request } from '@/lib/api'
import type {
  BulkResult,
  EntityDescriptor,
  ListGroup,
  ListPage,
  ListViewDefinition,
  SavedView,
} from './types'

/**
 * Клиент API настраиваемых списков (ТЗ §5.1).
 *
 * Страница запрашивается POST'ом, а не GET'ом с параметрами: набор фильтров — это
 * структура, и упаковка её в query string означала бы придумать кодировку, которую каждый
 * клиент реализует по-своему и слегка неправильно. Плюс длина URL кончается ровно на тех
 * представлениях, которые сохранять и интересно.
 *
 * Плата — запрос не кешируется и не кладётся в закладку. Ни то ни другое здесь не нужно:
 * ответ зависит от области видимости вызывающего, то есть общим кешем не был никогда, а в
 * закладку кладут представление, и у него есть id.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** Описание колонок списка: типы, сортируемость — основа конструктора фильтров. */
export function fetchColumns(entity: string, signal?: AbortSignal): Promise<EntityDescriptor> {
  return request<EntityDescriptor>(`/lists/${entity}/columns`, { signal })
}

/**
 * Страница списка.
 *
 * `cursor` — непрозрачный токен предыдущей страницы, а не её номер. Номер страницы — это
 * OFFSET в другой одежде, и именно он делает девятисотую страницу в девятьсот раз дороже
 * первой.
 */
export function fetchPage(
  entity: string,
  definition: ListViewDefinition,
  cursor?: string | null,
  signal?: AbortSignal,
): Promise<ListPage> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
  return request<ListPage>(`/lists/${entity}/page${query}`, {
    method: 'POST',
    body: definition,
    signal,
  })
}

/** Заголовки групп со счётчиками. */
export function fetchGroups(
  entity: string,
  definition: ListViewDefinition,
  signal?: AbortSignal,
): Promise<{ groups: ListGroup[] }> {
  return request(`/lists/${entity}/groups`, { method: 'POST', body: definition, signal })
}

/** Число строк под фильтром — чтобы предупредить перед выгрузкой. */
export function fetchCount(
  entity: string,
  definition: ListViewDefinition,
  signal?: AbortSignal,
): Promise<{ count: number }> {
  return request(`/lists/${entity}/count`, { method: 'POST', body: definition, signal })
}

/** Значения колонки для выпадающего списка в фильтре. */
export function fetchValues(
  entity: string,
  column: string,
  signal?: AbortSignal,
): Promise<{ values: unknown[] }> {
  return request(`/lists/${entity}/values?column=${encodeURIComponent(column)}`, { signal })
}

export function fetchViews(entity: string, signal?: AbortSignal): Promise<{ views: SavedView[] }> {
  return request(`/views/${entity}`, { signal })
}

export function createView(
  entity: string,
  name: string,
  definition: ListViewDefinition,
  shared: boolean,
): Promise<SavedView> {
  return request(`/views/${entity}`, { method: 'POST', body: { name, definition, shared } })
}

export function updateView(
  id: string,
  name: string,
  definition: ListViewDefinition,
): Promise<SavedView> {
  return request(`/views/${id}`, { method: 'PUT', body: { name, definition } })
}

export function makeViewDefault(id: string): Promise<SavedView> {
  return request(`/views/${id}/default`, { method: 'POST' })
}

export function deleteView(id: string): Promise<void> {
  return request(`/views/${id}`, { method: 'DELETE' })
}

export function bulkUpdate(
  entity: string,
  ids: string[],
  column: string,
  value: unknown,
): Promise<BulkResult> {
  return request(`/lists/${entity}/bulk/update`, {
    method: 'POST',
    body: { ids, column, value },
  })
}

export function bulkDelete(entity: string, ids: string[]): Promise<BulkResult> {
  return request(`/lists/${entity}/bulk/delete`, { method: 'POST', body: { ids } })
}

/**
 * Выгружает список файлом.
 *
 * Не через `request`: тот разбирает ответ как JSON, а здесь приходит бинарный CSV.
 * Скачивание запускается ссылкой на blob — сервер уже прислал `Content-Disposition`,
 * но fetch'ем полученный ответ браузер сам в файл не превратит.
 */
export async function exportList(
  entity: string,
  definition: ListViewDefinition,
  format: 'csv' | 'xlsx' = 'csv',
  columnHeaders: Record<string, string> = {},
): Promise<Blob> {
  const token = await accessToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(`${BASE_URL}/lists/${entity}/export?format=${format}`, {
    method: 'POST',
    headers,
    // Подписи колонок — те, что на экране (#88): сервер словаря не знает.
    body: JSON.stringify({ ...definition, headers: columnHeaders }),
  })

  if (response.status === 401) {
    await login()
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }
  if (!response.ok) {
    // Отказ приходит как problem+json даже на эндпоинте, который в успехе отдаёт файл:
    // «слишком много строк» — это сообщение, а не пустой файл.
    const problem = await response.json().catch(() => null)
    throw new ApiError(
      response.status,
      problem?.code ?? null,
      problem?.detail ?? problem?.message ?? response.statusText,
    )
  }
  return response.blob()
}


/**
 * Отдаёт blob пользователю файлом.
 *
 * Отдельной функцией, потому что забытый `revokeObjectURL` — это утечка на каждую
 * выгрузку, и замечают её через день работы, а не сразу.
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
